import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Card, CardBody, Alert, Badge } from 'reactstrap';
import onboardingSettingsService from '../../../services/onboardingSettingsService';
import LoadingSpinner from '../widgets/LoadingSpinner';

/**
 * OnboardingSettingsPanel - Generic "discover / confirm" self-service settings
 * picker used on ad platform OAuth success pages.
 *
 * Platform-agnostic: pass in `platform` + `fieldsConfig` (see
 * onboardingSettingsConfig.js) and it will:
 *   1. Call the platform's /onboarding/discover endpoint
 *   2. Pre-populate dropdowns/readonly fields from the `discovered` values
 *   3. Let the user change selections and call /onboarding/confirm
 *   4. Show saved/missing keys and full-activation status, and allow
 *      re-confirming as many times as needed.
 */
const OnboardingSettingsPanel = ({
  platform, accountId, token, fieldsConfig, platformDisplayName, onConfirmSuccess, onDiscovered,
  onKeepExisting,
}) => {
  const [loading, setLoading] = useState(true);
  const [discoverError, setDiscoverError] = useState(null);
  const [options, setOptions] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [selections, setSelections] = useState({});

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmResult, setConfirmResult] = useState(null);

  // True once discover returns and EVERY configured field for this platform
  // already has a value -- i.e. this account was fully confirmed before
  // (e.g. a re-onboarding user). Drives whether we offer a "Keep Previous
  // Settings" shortcut alongside Save, or only Save (nothing to keep yet).
  const [allFieldsComplete, setAllFieldsComplete] = useState(false);

  const { fields, intro } = fieldsConfig;

  useEffect(() => {
    let isMounted = true;

    const runDiscover = async () => {
      setLoading(true);
      setDiscoverError(null);
      try {
        const data = await onboardingSettingsService.discoverSettings({
          platform, accountId, token,
        });

        if (!isMounted) return;

        const discovered = data.discovered || {};
        setOptions(data.options || {});
        setWarnings(data.warnings || []);
        setSelections(discovered);

        const complete = fields.length > 0 && fields.every(field => !!discovered[field.key]);
        setAllFieldsComplete(complete);

        // Let the caller (e.g. the OAuth success page) know what was
        // discovered so it can decide whether this account has already
        // been confirmed before (e.g. a re-onboarding user) and tailor its
        // messaging/button labels accordingly.
        onDiscovered?.(discovered);
      } catch (err) {
        if (isMounted) setDiscoverError(err.message || 'Failed to load discovered settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (accountId) runDiscover();

    return () => {
      isMounted = false;
    };
  }, [platform, accountId, token]);

  const handleSelectChange = useCallback((key, selectedOption) => {
    setSelections(prev => ({ ...prev, [key]: selectedOption ? selectedOption.value : null }));
    setConfirmResult(null);
    // Once the user touches a selection, the "Keep Previous Settings"
    // shortcut no longer applies -- they need to explicitly Save instead.
    setAllFieldsComplete(false);
  }, []);

  const handleConfirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      const data = await onboardingSettingsService.confirmSettings({
        platform, accountId, selections, token,
      });
      setConfirmResult(data);
      // Let the caller (e.g. the admin status page's modal) know the save
      // succeeded so it can close the overlay and re-run the status check.
      if (data?.status === 'success') {
        onConfirmSuccess?.(data);
      }
    } catch (err) {
      setConfirmError(err.message || 'Failed to save your selections');
    } finally {
      setConfirming(false);
    }
  };

  // Everything discovered was already saved and unchanged from a prior
  // onboarding -- there's nothing new to persist, so just let the caller
  // (e.g. the OAuth success page) close the overlay without re-hitting
  // confirm/save.
  const handleKeepExisting = () => {
    onKeepExisting?.();
  };

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center">
          <LoadingSpinner active wrap />
          <p className="text-muted mb-0">Discovering your {platformDisplayName} settings…</p>
        </CardBody>
      </Card>
    );
  }

  if (discoverError) {
    return (
      <Card>
        <CardBody>
          <Alert color="danger">{discoverError}</Alert>
        </CardBody>
      </Card>
    );
  }

  const visibleFields = fields.filter(field => field.type !== 'hidden');
  const isFullyActivated = confirmResult?.metaAdApiStatus === true;

  return (
    <Card className="border-0">
      <CardBody>
        <div className="card__title text-center">
          <h5 className="bold-text">Confirm {platformDisplayName} Account Settings</h5>
        </div>
        <div className="mx-auto" style={{ maxWidth: '520px' }}>
          <Alert color="warning" className="text-center" style={{ padding: '16px 20px' }}>
            <strong style={{ fontSize: '1rem' }}>⚠ WARNING</strong>
            <br />
            <small>Incorrect selections can mix account data and cause display issues that are difficult to undo.</small>
          </Alert>

          <p className="text-muted text-center">{intro}</p>

          {warnings.length > 0 && (
            <Alert color="warning">
              <ul className="mb-0">
                {warnings.map(w => <li key={w}>{w}</li>)}
              </ul>
            </Alert>
          )}

          {visibleFields.map((field) => {
            const discoveredValue = selections[field.key];

            if (field.type === 'readonly') {
              const displayValue = field.buildLabel
                ? field.buildLabel(discoveredValue, options)
                : (discoveredValue || 'Not found');
              return (
                <div className="form__form-group" key={field.key}>
                  <span className="form__form-group-label">{field.label}</span>
                  <div className="form__form-group-field">
                    <p className="mb-0">{displayValue}</p>
                  </div>
                </div>
              );
            }

            const rawOptions = options[field.optionsKey] || [];
            const selectOptions = field.buildOptions(rawOptions);
            const selectedOption = selectOptions.find(o => o.value === discoveredValue) || null;

            return (
              <div className="form__form-group" key={field.key}>
                <span className="form__form-group-label">{field.label}</span>
                <div className="form__form-group-field">
                  <Select
                    classNamePrefix="react-select"
                    className="react-select"
                    value={selectedOption}
                    onChange={option => handleSelectChange(field.key, option)}
                    options={selectOptions}
                    placeholder={`Select ${field.label}...`}
                    isClearable={field.isClearable !== false}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  />
                  {selectOptions.length === 0 && (
                    <span className="form__form-group-description">
                      No {field.label.toLowerCase()} options were found for this account.
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {confirmError && <Alert color="danger">{confirmError}</Alert>}

          {confirmResult && (
            <Alert color={isFullyActivated ? 'success' : 'warning'}>
              {confirmResult.savedKeys?.length > 0 && (
                <p className="mb-1">
                  <strong>Saved:</strong> {confirmResult.savedKeys.join(', ')}
                </p>
              )}
              {confirmResult.missingKeys?.length > 0 && (
                <p className="mb-1">
                  <strong>Still needed:</strong> {confirmResult.missingKeys.join(', ')}
                </p>
              )}
              <p className="mb-0">
                {isFullyActivated ? (
                  <Badge color="success">Setup Complete — account fully activated</Badge>
                ) : (
                  <Badge color="warning">Saved — activation pending additional settings</Badge>
                )}
              </p>
            </Alert>
          )}

          <div className="form__form-group text-right">
            <LoadingSpinner active={confirming} />
            {!confirming && (
              <>
                {allFieldsComplete && !confirmResult && onKeepExisting && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary account__btn"
                    onClick={handleKeepExisting}
                    style={{ marginRight: '8px' }}
                  >
                    Keep Previous Settings
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary account__btn"
                  onClick={handleConfirm}
                >
                  {confirmResult ? 'Save Changes' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

OnboardingSettingsPanel.propTypes = {
  platform: PropTypes.string.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  token: PropTypes.string,
  platformDisplayName: PropTypes.string,
  fieldsConfig: PropTypes.shape({
    intro: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['select', 'readonly', 'hidden']).isRequired,
      optionsKey: PropTypes.string,
      buildOptions: PropTypes.func,
      buildLabel: PropTypes.func,
      // Defaults to true (clearable) when omitted; set to false for fields
      // that should never be blanked out (e.g. Meta's single Business).
      isClearable: PropTypes.bool,
    })).isRequired,
  }).isRequired,
  // Called with the confirm response after a successful save (status === 'success').
  onConfirmSuccess: PropTypes.func,
  // Called with the raw `discovered` map right after a successful discover
  // call, before the user has made/confirmed any selections.
  onDiscovered: PropTypes.func,
  // Called (no args) when the user clicks "Keep Previous Settings" -- only
  // offered when every field was already fully populated on discover and
  // hasn't been touched since. When omitted, that shortcut button is never
  // rendered and only Save/Save Changes is available.
  onKeepExisting: PropTypes.func,
};

OnboardingSettingsPanel.defaultProps = {
  token: null,
  platformDisplayName: 'Platform',
  onConfirmSuccess: null,
  onDiscovered: null,
  onKeepExisting: null,
};

export default OnboardingSettingsPanel;

